import {inject} from '@angular/core';
import {ESubjectType} from '@entities/subject';
import {Actions, ofType} from '@ngrx/effects';
import {Action, Store} from '@ngrx/store';
import {IS_BROWSER} from '@shared/constants/is-browser.token';
import {ErrorsHelper} from '@shared/helper';
import {OdApiErrorResponse} from '@shared/services';
import {createFunctionalEffect} from '@shared/utils/effects.helper';
import {AuthenticationActions} from '@state/authentication/authentication.actions';
import {authenticationFeature} from '@state/authentication/authentication.feature';
import {HistoryActions} from '@state/history';
import {SearchActions} from '@state/search';
import {
  EApiNotification,
  mapOdApiActions,
} from '@utils/map-od-api-actions.function';
import {
  catchError,
  filter,
  map,
  of,
  switchMap,
  takeUntil,
  withLatestFrom,
} from 'rxjs';

import {
  createFavoriteToggleEffect,
  createSyncEffect,
} from './dossier-effect.factories';
import {SubjectActions} from './subject.actions';
import {SubjectApi} from './subject.api';
import {subjectFeature} from './subject.feature';
import {ISubjectRequest} from './subject-state.interface';

export namespace SubjectEffects {
  const SYNC_POLL_INTERVAL_MS = 1000;

  export const getSubject$ = createFunctionalEffect(
    (actions$ = inject(Actions), api = inject(SubjectApi)) =>
      actions$.pipe(
        ofType(SubjectActions.getSubject.requested),
        switchMap(({subjectId, subjectType}) =>
          api
            .getSubject(subjectId, subjectType)
            .pipe(
              mapOdApiActions(
                SubjectActions.getSubject,
                EApiNotification.OnErrorExceptNotFound
              ),
              takeUntil(actions$.pipe(ofType(SubjectActions.clearSubject)))
            )
        )
      )
  );

  export const loadSubjectRelatedData$ = createFunctionalEffect(
    (
      actions$ = inject(Actions),
      store = inject(Store),
      isBrowser = inject(IS_BROWSER)
    ) =>
      actions$.pipe(
        ofType(SubjectActions.getSubject.succeeded),
        withLatestFrom(
          store.select(authenticationFeature.selectIsAuthenticated)
        ),
        switchMap(([{payload}, isAuthenticated]) => {
          const resultActions: Action[] = [];

          if (isBrowser) {
            resultActions.push(
              SubjectActions.getLinkedSubjects.requested({
                subjectId: payload.id,
              })
            );
          }

          if (isAuthenticated) {
            resultActions.push(
              SubjectActions.getSubjectFavorites.requested({
                subjectId: payload.id,
              }),
              HistoryActions.saveToHistory.requested({subjectId: payload.id})
            );
          }

          return resultActions;
        })
      )
  );

  export const getFavorites$ = createFunctionalEffect(
    (actions$ = inject(Actions), api = inject(SubjectApi)) =>
      actions$.pipe(
        ofType(SubjectActions.getSubjectFavorites.requested),
        switchMap(({subjectId}) =>
          api.getSubjectFavorites(subjectId).pipe(
            map((value) => ({data: value})),
            mapOdApiActions(SubjectActions.getSubjectFavorites),
            takeUntil(actions$.pipe(ofType(SubjectActions.clearSubject)))
          )
        )
      )
  );

  export const markFavorite$ = createFavoriteToggleEffect({
    trigger: SubjectActions.markFavorite,
    failed: SubjectActions.markFavoriteFailed,
    request: (api, subjectId) => api.markFavorite(subjectId),
    successMessage: 'dossier.favorite-added',
  });

  export const unmarkFavorite$ = createFavoriteToggleEffect({
    trigger: SubjectActions.unmarkFavorite,
    failed: SubjectActions.unmarkFavoriteFailed,
    request: (api, subjectId) => api.unmarkFavorite(subjectId),
    successMessage: 'dossier.favorite-removed',
  });

  export const syncSubject$ = createSyncEffect({
    syncActions: SubjectActions.subjectSync,
    clearAction: SubjectActions.clearSubject,
    pollIntervalMs: SYNC_POLL_INTERVAL_MS,
  });

  export const syncSubjectSucceeded$ = createFunctionalEffect(
    (actions$ = inject(Actions), store = inject(Store)) =>
      actions$.pipe(
        ofType(SubjectActions.subjectSync.succeeded),
        map(({payload}) => {
          store.dispatch(SearchActions.clearSubjects());

          const {subjectType, subjectId, code} = payload;

          return SubjectActions.getSubject.requested({
            subjectId:
              subjectType === ESubjectType.Physical
                ? subjectId.toString()
                : code,
            subjectType,
          });
        })
      )
  );

  export const getLinkedSubjects$ = createFunctionalEffect(
    (actions$ = inject(Actions), api = inject(SubjectApi)) =>
      actions$.pipe(
        ofType(SubjectActions.getLinkedSubjects.requested),
        switchMap(({subjectId}) =>
          api
            .getLinkedSubjects(subjectId)
            .pipe(mapOdApiActions(SubjectActions.getLinkedSubjects))
        )
      )
  );

  export const getRelatedLinks$ = createFunctionalEffect(
    (actions$ = inject(Actions), api = inject(SubjectApi)) =>
      actions$.pipe(
        ofType(SubjectActions.getRelatedLinks.requested),
        switchMap(({subjectId}) =>
          api
            .getRelatedLinks(subjectId)
            .pipe(
              mapOdApiActions(SubjectActions.getRelatedLinks),
              takeUntil(actions$.pipe(ofType(SubjectActions.clearSubject)))
            )
        )
      )
  );

  export const getBeneficiaries$ = createFunctionalEffect(
    (actions$ = inject(Actions), api = inject(SubjectApi)) =>
      actions$.pipe(
        ofType(SubjectActions.getBeneficiaries.requested),
        switchMap(({subjectId}) =>
          api
            .getBeneficiaries(subjectId)
            .pipe(
              mapOdApiActions(SubjectActions.getBeneficiaries),
              takeUntil(actions$.pipe(ofType(SubjectActions.clearSubject)))
            )
        )
      )
  );

  export const getFounders$ = createFunctionalEffect(
    (actions$ = inject(Actions), api = inject(SubjectApi)) =>
      actions$.pipe(
        ofType(SubjectActions.getFounders.requested),
        switchMap(({subjectId}) =>
          api
            .getFounders(subjectId)
            .pipe(
              mapOdApiActions(SubjectActions.getFounders),
              takeUntil(actions$.pipe(ofType(SubjectActions.clearSubject)))
            )
        )
      )
  );

  export const getBranches$ = createFunctionalEffect(
    (actions$ = inject(Actions), api = inject(SubjectApi)) =>
      actions$.pipe(
        ofType(SubjectActions.getBranches.requested),
        switchMap(({subjectId}) =>
          api
            .getBranches(subjectId)
            .pipe(
              mapOdApiActions(SubjectActions.getBranches),
              takeUntil(actions$.pipe(ofType(SubjectActions.clearSubject)))
            )
        )
      )
  );

  export const getEnforcements$ = createFunctionalEffect(
    (actions$ = inject(Actions), api = inject(SubjectApi)) =>
      actions$.pipe(
        ofType(SubjectActions.getEnforcements.requested),
        switchMap((payload) =>
          api.getEnforcements(payload).pipe(
            map((response) =>
              SubjectActions.getEnforcements.succeeded({
                payload: response.data,
                meta: {
                  birthdateFilterAvailable:
                    response.meta.birthdateFilterAvailable,
                  birthdateFilterApplied: response.meta.birthdateFilterApplied,
                },
                pagination: response.meta.pagination,
              })
            ),
            catchError((e: OdApiErrorResponse) =>
              of(
                SubjectActions.getEnforcements.failed({
                  error: ErrorsHelper.getErrorLabelsFromIApi(e),
                })
              )
            ),
            takeUntil(actions$.pipe(ofType(SubjectActions.clearSubject)))
          )
        )
      )
  );

  export const getActivities$ = createFunctionalEffect(
    (actions$ = inject(Actions), api = inject(SubjectApi)) =>
      actions$.pipe(
        ofType(SubjectActions.getActivities.requested),
        switchMap(({subjectId}) =>
          api
            .getActivities(subjectId)
            .pipe(
              mapOdApiActions(SubjectActions.getActivities),
              takeUntil(actions$.pipe(ofType(SubjectActions.clearSubject)))
            )
        )
      )
  );

  export const reloadSubjectOnLogin$ = createFunctionalEffect(
    (actions$ = inject(Actions), store = inject(Store)) =>
      actions$.pipe(
        ofType(AuthenticationActions.loginConfirm.succeeded),
        withLatestFrom(store.select(subjectFeature.selectSubjectRequest)),
        map(([, request]) => request),
        filter((request): request is ISubjectRequest => !!request),
        map((request) => SubjectActions.getSubject.requested(request))
      )
  );

  export const reloadSubjectOnLogout$ = createFunctionalEffect(
    (actions$ = inject(Actions), store = inject(Store)) =>
      actions$.pipe(
        ofType(AuthenticationActions.logout),
        withLatestFrom(store.select(subjectFeature.selectSubjectRequest)),
        map(([, request]) => request),
        filter((request): request is ISubjectRequest => !!request),
        switchMap((request) => [
          SubjectActions.clearSubject(),
          SubjectActions.getSubject.requested(request),
        ])
      )
  );
}
